import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Plus, Minus, Trash2, Printer, X, ArrowLeft, Gift, User, QrCode as QrCodeIcon, Banknote, CheckCircle, Loader2, Camera, CameraOff, Ticket, Settings, Bluetooth, Smartphone, CreditCard, RotateCcw, Clock, XCircle, Volume2, Users, ShoppingCart } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const formatPrice = (p) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(p);
const LOGO = "https://customer-assets.emergentagent.com/job_0890dc80-6146-4c2d-b11f-fa0b2de6d70b/artifacts/ekzdqal8_image.png";

// =================== PRINTER CONFIG ===================
const DEFAULT_PRINTER = { paperSize: "58", useRawBT: false, printerName: "" };

function loadPrinterConfig() {
  try {
    const saved = localStorage.getItem("fi_pos_printer");
    return saved ? { ...DEFAULT_PRINTER, ...JSON.parse(saved) } : DEFAULT_PRINTER;
  } catch { return DEFAULT_PRINTER; }
}

function savePrinterConfig(config) {
  localStorage.setItem("fi_pos_printer", JSON.stringify(config));
}

// =================== PRINTER SETTINGS DIALOG ===================
function PrinterSettingsDialog({ open, onClose }) {
  const [config, setConfig] = useState(loadPrinterConfig);

  const update = (key, val) => {
    const next = { ...config, [key]: val };
    setConfig(next);
    savePrinterConfig(next);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border-[#262626] text-white max-w-md rounded-none" data-testid="printer-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
            <Settings size={18} className="text-[#E60000]" /> Pengaturan Printer
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 mt-2">
          {/* Paper Size */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">Ukuran Kertas</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: "58", label: "58mm", desc: "32 karakter/baris" },
                { value: "80", label: "80mm", desc: "48 karakter/baris" },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => update("paperSize", opt.value)}
                  className={`p-3 border text-left transition-colors ${config.paperSize === opt.value ? "border-[#E60000] bg-[#E60000]/10" : "border-[#262626] bg-[#111] hover:border-[#444]"}`}
                  data-testid={`paper-${opt.value}`}
                >
                  <p className={`text-sm font-bold ${config.paperSize === opt.value ? "text-[#E60000]" : "text-white"}`}>{opt.label}</p>
                  <p className="text-[10px] text-gray-500">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* RawBT Connection */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">Koneksi Printer</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => update("useRawBT", false)}
                className={`p-3 border text-left transition-colors ${!config.useRawBT ? "border-[#E60000] bg-[#E60000]/10" : "border-[#262626] bg-[#111] hover:border-[#444]"}`}
                data-testid="conn-browser"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Printer size={14} className={!config.useRawBT ? "text-[#E60000]" : "text-gray-500"} />
                  <span className={`text-sm font-bold ${!config.useRawBT ? "text-[#E60000]" : "text-white"}`}>Browser Print</span>
                </div>
                <p className="text-[10px] text-gray-500">Cetak via dialog browser (Windows/Mac/USB)</p>
              </button>
              <button
                onClick={() => update("useRawBT", true)}
                className={`p-3 border text-left transition-colors ${config.useRawBT ? "border-[#E60000] bg-[#E60000]/10" : "border-[#262626] bg-[#111] hover:border-[#444]"}`}
                data-testid="conn-rawbt"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Bluetooth size={14} className={config.useRawBT ? "text-[#E60000]" : "text-gray-500"} />
                  <span className={`text-sm font-bold ${config.useRawBT ? "text-[#E60000]" : "text-white"}`}>RawBT</span>
                </div>
                <p className="text-[10px] text-gray-500">Bluetooth thermal via RawBT (Android)</p>
              </button>
            </div>
          </div>

          {/* RawBT Info */}
          {config.useRawBT && (
            <div className="bg-[#111] border border-[#262626] p-3 space-y-2">
              <div className="flex items-center gap-2 text-[#E60000]">
                <Smartphone size={14} />
                <span className="text-xs font-bold uppercase">Cara Setup RawBT</span>
              </div>
              <ol className="text-[10px] text-gray-400 space-y-1 list-decimal list-inside">
                <li>Install <span className="text-white">RawBT Print Service</span> dari Google Play Store</li>
                <li>Buka RawBT, pilih <span className="text-white">Bluetooth</span> sebagai koneksi</li>
                <li>Pair printer thermal Anda di pengaturan Bluetooth Android</li>
                <li>Pilih printer di RawBT lalu tes cetak</li>
                <li>Klik <span className="text-white">Cetak Struk</span> &rarr; pilih <span className="text-white">RawBT</span> di dialog cetak Chrome</li>
              </ol>
              <div className="pt-1">
                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1 block">Nama Printer (opsional)</label>
                <Input
                  className="bg-[#1A1A1A] border-[#262626] text-white h-8 rounded-none text-xs"
                  placeholder="Contoh: BlueTooth Printer"
                  value={config.printerName}
                  onChange={e => update("printerName", e.target.value)}
                  data-testid="printer-name-input"
                />
              </div>
            </div>
          )}

          {/* Status */}
          <div className="bg-[#0F0F0F] border border-[#262626] p-3 flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-400">Konfigurasi Aktif</p>
              <p className="text-white text-sm font-semibold">{config.paperSize}mm | {config.useRawBT ? "RawBT Bluetooth" : "Browser Print"}</p>
            </div>
            <Badge className="bg-green-900/30 text-green-400 border-green-800/40">Tersimpan</Badge>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// =================== BARCODE SCANNER ===================
function BarcodeScanner({ onScan, onClose }) {
  const scannerRef = useRef(null);
  const html5QrcodeRef = useRef(null);
  const onScanRef = useRef(onScan);
  const stoppedRef = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => { onScanRef.current = onScan; }, [onScan]);

  useEffect(() => {
    let mounted = true;
    stoppedRef.current = false;

    const stopScanner = () => {
      if (stoppedRef.current) return;
      stoppedRef.current = true;
      const s = html5QrcodeRef.current;
      if (s) {
        try {
          const state = s.getState();
          // 2 = SCANNING, 3 = PAUSED
          if (state === 2 || state === 3) {
            s.stop().catch(() => {});
          }
        } catch {}
      }
    };

    const startScanner = async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        if (!mounted) return;
        const scanner = new Html5Qrcode("barcode-reader");
        html5QrcodeRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 200, height: 200 } },
          (decodedText) => {
            stopScanner();
            onScanRef.current(decodedText);
          },
          () => {}
        );
      } catch {
        if (mounted) setError("Tidak dapat mengakses kamera. Pastikan izin kamera diaktifkan dan gunakan HTTPS.");
      }
    };
    startScanner();
    return () => {
      mounted = false;
      stopScanner();
      html5QrcodeRef.current = null;
    };
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-400 flex items-center gap-1.5"><Camera size={12} /> Arahkan kamera ke QR code kupon</p>
        <button onClick={onClose} className="text-gray-500 hover:text-white"><CameraOff size={16} /></button>
      </div>
      <div id="barcode-reader" ref={scannerRef} className="w-full overflow-hidden rounded-sm border border-[#262626]" />
      {error && <p className="text-red-400 text-xs">{error}</p>}
    </div>
  );
}

// =================== REDEEM PANEL ===================
const REQUIRED_COUPONS = 5;

function RedeemPanel({ onRedeemSuccess }) {
  const [code, setCode] = useState("");
  const [scanning, setScanning] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [collectedCoupons, setCollectedCoupons] = useState([]); // [{coupon, customer, message}]
  const [processing, setProcessing] = useState(false);
  const [selectedCapster, setSelectedCapster] = useState("");
  const [capsters, setCapsters] = useState([]);
  const [lastError, setLastError] = useState("");

  useEffect(() => { api.get("/capsters").then(({ data }) => setCapsters(data)).catch(() => {}); }, []);

  const addCoupon = async (couponCode) => {
    const trimmed = (couponCode || code).trim();
    if (!trimmed) return toast.error("Masukkan kode kupon/struk");
    // Check duplicate
    if (collectedCoupons.some(c => c.coupon.coupon_code === trimmed || c.coupon.id === trimmed)) {
      return toast.error("Kupon ini sudah di-scan");
    }
    setVerifying(true); setLastError("");
    try {
      const { data } = await api.get(`/loyalty/verify-coupon/${trimmed}`);
      if (data.valid && data.coupon) {
        // Check duplicate by coupon id
        if (collectedCoupons.some(c => c.coupon.id === data.coupon.id)) {
          toast.error("Kupon ini sudah terdaftar"); return;
        }
        setCollectedCoupons(prev => [...prev, { coupon: data.coupon, customer: data.customer, message: data.message }]);
        setCode("");
        toast.success(`${collectedCoupons.length + 1}/${REQUIRED_COUPONS} - ${data.message}`);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Kupon tidak valid";
      setLastError(msg); toast.error(msg);
    } finally { setVerifying(false); }
  };

  const removeCoupon = (idx) => {
    setCollectedCoupons(prev => prev.filter((_, i) => i !== idx));
  };

  const handleScan = (decodedText) => {
    setScanning(false);
    setCode(decodedText);
    addCoupon(decodedText);
  };

  const handleRedeem = async () => {
    if (collectedCoupons.length < REQUIRED_COUPONS) return toast.error(`Minimal ${REQUIRED_COUPONS} kupon diperlukan`);
    setProcessing(true);
    try {
      // Get first service for free
      const svcRes = await api.get("/services");
      const svc = svcRes.data.find(s => s.category === "potong") || svcRes.data[0];
      if (!svc) { toast.error("Tidak ada layanan potong tersedia"); return; }
      const firstCustomer = collectedCoupons[0].customer;
      const { data } = await api.post("/transactions", {
        customer_id: firstCustomer?.id || null,
        customer_name: firstCustomer?.name || "Walk-in",
        customer_phone: firstCustomer?.phone || "",
        items: [{ id: svc.id, name: svc.name, price: svc.price, qty: 1, category: svc.category || "potong" }],
        payment_method: "kupon", capster_id: selectedCapster,
        loyalty_coupon_ids: collectedCoupons.map(c => c.coupon.id),
        cash_received: 0, cash_change: 0,
      });
      const couponCodes = collectedCoupons.map(c => c.coupon.coupon_code).join(", ");
      onRedeemSuccess({ ...data, cash_received: 0, cash_change: 0, redeem_coupon_code: couponCodes });
      setCollectedCoupons([]); setCode(""); setSelectedCapster(""); setLastError("");
      toast.success("5 kupon berhasil di-redeem! Potong gratis!");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setProcessing(false); }
  };

  const resetAll = () => { setCollectedCoupons([]); setCode(""); setLastError(""); };

  return (
    <div className="p-4 space-y-4" data-testid="redeem-panel">
      <div className="bg-[#0F0F0F] border border-[#262626] p-4 space-y-4">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Ticket size={16} className="text-[#E60000]" />
            <h3 className="text-white text-sm font-bold uppercase tracking-wider">Redeem Kupon Loyalitas</h3>
          </div>
          {collectedCoupons.length > 0 && (
            <button onClick={resetAll} className="text-xs text-gray-500 hover:text-red-400">Reset</button>
          )}
        </div>
        <p className="text-gray-500 text-xs">Kumpulkan & scan <span className="text-white font-bold">{REQUIRED_COUPONS} kupon</span> valid untuk redeem 1x potong gratis.</p>

        {/* Progress */}
        <div className="flex gap-1">
          {Array.from({ length: REQUIRED_COUPONS }).map((_, i) => (
            <div key={i} className={`h-2 flex-1 transition-colors ${i < collectedCoupons.length ? "bg-[#E60000]" : "bg-[#262626]"}`} />
          ))}
        </div>
        <p className="text-center text-xs">
          <span className={collectedCoupons.length >= REQUIRED_COUPONS ? "text-green-400 font-bold" : "text-gray-400"}>
            {collectedCoupons.length}/{REQUIRED_COUPONS} kupon terkumpul
          </span>
        </p>

        {/* Input */}
        {collectedCoupons.length < REQUIRED_COUPONS && (
          <>
            <div className="flex gap-2">
              <Input className="bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none flex-1 font-mono" placeholder="Scan/input kode kupon atau struk..." value={code} onChange={e => setCode(e.target.value)} onKeyDown={e => e.key === "Enter" && addCoupon()} data-testid="redeem-code-input" />
              <Button onClick={() => addCoupon()} disabled={verifying} className="bg-[#E60000] hover:bg-[#FF1A1A] text-white h-10 rounded-none px-4" data-testid="verify-coupon-btn">
                {verifying ? <Loader2 size={14} className="animate-spin" /> : "Tambah"}
              </Button>
              <Button onClick={() => setScanning(!scanning)} className={`h-10 rounded-none px-3 ${scanning ? "bg-[#E60000] text-white" : "bg-[#262626] hover:bg-[#333] text-white"}`} data-testid="toggle-camera-btn"><Camera size={16} /></Button>
            </div>
            {scanning && <BarcodeScanner onScan={handleScan} onClose={() => setScanning(false)} />}
            {lastError && <p className="text-red-400 text-xs flex items-center gap-1"><X size={12} /> {lastError}</p>}
          </>
        )}

        {/* Collected coupons list */}
        {collectedCoupons.length > 0 && (
          <div className="space-y-1.5">
            {collectedCoupons.map((c, idx) => (
              <div key={c.coupon.id} className="flex items-center justify-between bg-[#0A0A0A] border border-[#1F1F1F] px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="text-[#E60000] font-bold text-xs w-5">{idx + 1}.</span>
                  <div>
                    <p className="text-white text-xs font-mono">{c.coupon.coupon_code}</p>
                    <p className="text-gray-500 text-[10px]">{c.customer?.name || "-"}</p>
                  </div>
                </div>
                <button onClick={() => removeCoupon(idx)} className="text-gray-500 hover:text-red-400" data-testid={`remove-coupon-${idx}`}><X size={14} /></button>
              </div>
            ))}
          </div>
        )}

        {/* Redeem section - show when 5 coupons collected */}
        {collectedCoupons.length >= REQUIRED_COUPONS && (
          <div className="border border-green-800/40 bg-green-900/10 p-4 space-y-3">
            <div className="flex items-center gap-2 text-green-400"><CheckCircle size={16} /><span className="text-sm font-bold">{REQUIRED_COUPONS} Kupon Terkumpul!</span></div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Capster</label>
              <Select value={selectedCapster} onValueChange={setSelectedCapster}>
                <SelectTrigger className="bg-[#1A1A1A] border-[#262626] text-white h-9 rounded-none text-xs mt-1" data-testid="redeem-capster-select"><SelectValue placeholder="Pilih capster..." /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#262626]">{capsters.map(c => <SelectItem key={c.id} value={c.id} className="text-white text-xs">{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={handleRedeem} disabled={processing} className="w-full bg-green-700 hover:bg-green-600 text-white font-bold uppercase tracking-wider h-11 rounded-none" data-testid="redeem-btn">
              {processing ? "Memproses..." : <><Gift size={14} className="mr-2" /> Redeem {REQUIRED_COUPONS} Kupon - Potong Gratis</>}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// =================== QUEUE PANEL ===================
function speakNumber(num, name) {
  if (!window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const text = `Nomor antrian ${num}${name ? `, atas nama ${name}` : ""}, silakan menuju kasir`;
  const utter = new SpeechSynthesisUtterance(text);
  utter.lang = "id-ID";
  utter.rate = 0.9;
  utter.pitch = 1;
  utter.volume = 1;
  // Try to find Indonesian voice
  const voices = window.speechSynthesis.getVoices();
  const idVoice = voices.find(v => v.lang.startsWith("id")) || voices.find(v => v.lang.startsWith("en"));
  if (idVoice) utter.voice = idVoice;
  window.speechSynthesis.speak(utter);
}

function QueuePanel({ onQueueDone }) {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [serviceType, setServiceType] = useState("Potong Rambut");
  const [creating, setCreating] = useState(false);
  const [phoneLooking, setPhoneLooking] = useState(false);

  const fetchQueue = useCallback(async () => {
    try { const { data } = await api.get("/queue/today"); setQueue(data); } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchQueue(); const iv = setInterval(fetchQueue, 5000); return () => clearInterval(iv); }, [fetchQueue]);
  useEffect(() => { window.speechSynthesis?.getVoices(); }, []);

  const lookupPhone = async () => {
    if (!customerPhone.trim() || customerPhone.length < 4) return;
    setPhoneLooking(true);
    try {
      const { data } = await api.get(`/customers/search?phone=${customerPhone.trim()}`);
      if (data.customer) {
        setCustomerName(data.customer.name);
        toast.success(`Member ditemukan: ${data.customer.name}`);
      }
    } catch {
      // Not found - that's ok
    } finally { setPhoneLooking(false); }
  };

  const createTicket = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/queue/create", { customer_name: customerName.trim(), customer_phone: customerPhone.trim(), service_type: serviceType });
      setCustomerName(""); setCustomerPhone("");
      toast.success(`Antrian #${data.queue_number} dibuat`);
      fetchQueue();
      printQueueTicket(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); } finally { setCreating(false); }
  };

  const callNext = async () => {
    try {
      const { data } = await api.post("/queue/call-next");
      toast.success(`Memanggil nomor ${data.queue_number}`);
      speakNumber(data.queue_number, data.customer_name);
      setTimeout(() => speakNumber(data.queue_number, data.customer_name), 4000);
      fetchQueue();
    } catch (err) { toast.error(err.response?.data?.detail || "Tidak ada antrian"); }
  };

  const callSpecific = async (q) => {
    try {
      await api.put(`/queue/${q.id}/call`);
      speakNumber(q.queue_number, q.customer_name);
      setTimeout(() => speakNumber(q.queue_number, q.customer_name), 4000);
      fetchQueue();
    } catch {}
  };

  const markDone = async (q) => {
    try {
      const { data } = await api.put(`/queue/${q.id}/done`);
      fetchQueue();
      toast.success(`Antrian #${q.queue_number} selesai`);
      // Switch to transaction mode with customer data
      if (onQueueDone) {
        onQueueDone({
          customer_name: q.customer_name || "",
          customer_phone: q.customer_phone || "",
          customer_id: q.customer_id || "",
        });
      }
    } catch {}
  };

  const updateStatus = async (qid, status) => {
    try { await api.put(`/queue/${qid}/${status}`); fetchQueue(); } catch {}
  };

  const printQueueTicket = (q) => {
    const html = `<!DOCTYPE html><html><head><title>Antrian #${q.queue_number}</title>
<style>@page{size:80mm auto;margin:0}*{margin:0;padding:0;box-sizing:border-box}body{font-family:monospace;text-align:center;padding:6mm;color:#000;background:#fff}</style>
</head><body>
<div style="font-weight:bold;font-size:14px;margin-bottom:2px">FI BARBERSHOP SEKIP</div>
<div style="font-size:8px;color:#666;margin-bottom:8px">Jl. Ampibi, Kec. Kemuning, Palembang</div>
<div style="font-size:9px;color:#666;margin-bottom:8px">Nomor Antrian</div>
<div style="font-size:64px;font-weight:bold;line-height:1;margin:8px 0">${String(q.queue_number).padStart(3,"0")}</div>
${q.customer_name ? `<div style="font-size:12px;margin:4px 0">${q.customer_name}</div>` : ""}
<div style="font-size:10px;color:#666;margin:4px 0">${q.service_type || "Potong Rambut"}</div>
<div style="border-top:1px dashed #000;margin:8px 0;padding-top:6px;font-size:9px;color:#999">${new Date(q.created_at).toLocaleString("id-ID")}</div>
<div style="font-size:8px;color:#999;margin-top:4px">Mohon tunggu hingga nomor Anda dipanggil</div>
</body></html>`;
    printViaRawBT(html, `Antrian #${q.queue_number}`);
  };

  const waiting = queue.filter(q => q.status === "waiting");
  const serving = queue.filter(q => q.status === "serving");
  const done = queue.filter(q => q.status === "done" || q.status === "skipped");
  const currentNumber = serving[0] || null;

  if (loading) return <div className="flex items-center justify-center h-40"><Loader2 size={24} className="text-[#E60000] animate-spin" /></div>;

  return (
    <div className="p-4 space-y-4" data-testid="queue-panel">
      <div className="bg-[#0F0F0F] border border-[#262626] p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white text-sm font-bold uppercase tracking-wider flex items-center gap-2"><Users size={16} className="text-[#E60000]" /> Antrian Hari Ini</h3>
          <div className="flex items-center gap-3 text-xs">
            <span className="text-yellow-400">{waiting.length} menunggu</span>
            <span className="text-blue-400">{serving.length} dilayani</span>
            <span className="text-green-400">{done.length} selesai</span>
          </div>
        </div>
        <div className="bg-[#050505] border border-[#262626] p-6 flex flex-col items-center mb-4">
          <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Sedang Dilayani</p>
          {currentNumber ? (
            <>
              <p className="text-[#E60000] font-heading text-7xl font-bold leading-none">{String(currentNumber.queue_number).padStart(3, "0")}</p>
              {currentNumber.customer_name && <p className="text-white text-sm mt-2">{currentNumber.customer_name}</p>}
              <p className="text-gray-500 text-[10px] mt-1">{currentNumber.service_type}</p>
            </>
          ) : (
            <p className="text-gray-600 text-2xl font-heading">---</p>
          )}
        </div>
        <Button onClick={callNext} disabled={waiting.length === 0} className="w-full bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase tracking-wider h-12 rounded-none text-base disabled:opacity-30" data-testid="call-next-btn">
          <Volume2 size={18} className="mr-2" /> Panggil Berikutnya {waiting.length > 0 && `(#${String(waiting[0].queue_number).padStart(3, "0")})`}
        </Button>
      </div>
      <div className="bg-[#0F0F0F] border border-[#262626] p-4 space-y-3">
        <h3 className="text-gray-500 text-[10px] font-bold uppercase tracking-wider">Buat Nomor Antrian Baru</h3>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Input className="bg-[#1A1A1A] border-[#262626] text-white h-9 rounded-none text-xs pr-14" placeholder="No. HP (opsional)..." value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} onBlur={lookupPhone} onKeyDown={e => e.key === "Enter" && lookupPhone()} data-testid="queue-phone-input" />
            {phoneLooking && <Loader2 size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 animate-spin" />}
          </div>
          <Input className="bg-[#1A1A1A] border-[#262626] text-white h-9 rounded-none flex-1 text-xs" placeholder="Nama customer..." value={customerName} onChange={e => setCustomerName(e.target.value)} data-testid="queue-name-input" />
        </div>
        <div className="flex gap-2">
          <Select value={serviceType} onValueChange={setServiceType}>
            <SelectTrigger className="bg-[#1A1A1A] border-[#262626] text-white h-9 rounded-none flex-1 text-xs" data-testid="queue-service-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#262626]">
              <SelectItem value="Potong Rambut" className="text-white text-xs">Potong Rambut</SelectItem>
              <SelectItem value="Styling" className="text-white text-xs">Styling</SelectItem>
              <SelectItem value="Coloring" className="text-white text-xs">Coloring</SelectItem>
              <SelectItem value="Treatment" className="text-white text-xs">Treatment</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={createTicket} disabled={creating} className="bg-[#E60000] hover:bg-[#FF1A1A] text-white h-9 rounded-none text-xs px-4" data-testid="create-queue-btn">
            {creating ? <Loader2 size={14} className="animate-spin" /> : <><Printer size={14} className="mr-1" /> Cetak</>}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-[#0F0F0F] border border-[#262626]">
          <div className="px-4 py-2.5 border-b border-[#262626]"><span className="text-yellow-400 text-xs font-bold uppercase tracking-wider">Menunggu ({waiting.length})</span></div>
          <div className="divide-y divide-[#1A1A1A] max-h-60 overflow-auto">
            {waiting.length === 0 && <p className="text-gray-600 text-xs text-center py-4">Tidak ada antrian</p>}
            {waiting.map(q => (
              <div key={q.id} className="px-4 py-2 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-white font-bold font-mono text-sm w-8">{String(q.queue_number).padStart(3, "0")}</span>
                  <div><p className="text-gray-300 text-xs">{q.customer_name || "-"}</p><p className="text-gray-600 text-[10px]">{q.service_type}</p></div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => callSpecific(q)} className="text-[10px] text-blue-400 hover:text-blue-300 bg-blue-900/20 px-2 py-1" data-testid={`call-${q.id}`}>Panggil</button>
                  <button onClick={() => updateStatus(q.id, "skip")} className="text-[10px] text-gray-500 hover:text-gray-400 bg-gray-800/20 px-2 py-1">Skip</button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-[#0F0F0F] border border-[#262626]">
          <div className="px-4 py-2.5 border-b border-[#262626]"><span className="text-blue-400 text-xs font-bold uppercase tracking-wider">Sedang Dilayani ({serving.length})</span></div>
          <div className="divide-y divide-[#1A1A1A] max-h-60 overflow-auto">
            {serving.length === 0 && <p className="text-gray-600 text-xs text-center py-4">Tidak ada</p>}
            {serving.map(q => (
              <div key={q.id} className="px-4 py-2 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-[#E60000] font-bold font-mono text-sm w-8">{String(q.queue_number).padStart(3, "0")}</span>
                  <div><p className="text-gray-300 text-xs">{q.customer_name || "-"}</p><p className="text-gray-600 text-[10px]">{q.service_type}</p></div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { speakNumber(q.queue_number, q.customer_name); }} className="text-[10px] text-yellow-400 hover:text-yellow-300 bg-yellow-900/20 px-2 py-1">Ulang</button>
                  <button onClick={() => markDone(q)} className="text-[10px] text-green-400 hover:text-green-300 bg-green-900/20 px-2 py-1">Selesai</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// =================== REFUND POS PANEL ===================
function RefundPOSPanel() {
  const [code, setCode] = useState("");
  const [scanning, setScanning] = useState(false);
  const [checking, setChecking] = useState(false);
  const [txData, setTxData] = useState(null);
  const [hoursLeft, setHoursLeft] = useState(0);
  const [error, setError] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [refundId, setRefundId] = useState(null);
  const [refundStatus, setRefundStatus] = useState(null); // null | "pending" | "approved" | "rejected"
  const [approvalInfo, setApprovalInfo] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => { return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, []);

  const checkTx = async (txCode) => {
    const trimmed = (txCode || code).trim();
    if (!trimmed) return toast.error("Masukkan kode transaksi");
    setChecking(true); setError(""); setTxData(null); setRefundId(null); setRefundStatus(null);
    try {
      const { data } = await api.get(`/pos/refund-check/${trimmed}`);
      setTxData(data.transaction);
      setHoursLeft(data.hours_left);
      setCode(trimmed);
      toast.success(data.message);
    } catch (err) {
      setError(err.response?.data?.detail || "Transaksi tidak valid");
      toast.error(err.response?.data?.detail || "Gagal");
    } finally { setChecking(false); }
  };

  const handleScan = (decodedText) => { setScanning(false); setCode(decodedText); checkTx(decodedText); };

  const submitRefund = async () => {
    if (!reason.trim()) return toast.error("Alasan refund wajib diisi");
    setSubmitting(true);
    try {
      const { data } = await api.post("/pos/refund-submit", { transaction_code: code, reason: reason.trim() });
      setRefundId(data.id);
      setRefundStatus("pending");
      toast.success("Pengajuan refund terkirim. Menunggu approval Kepala Cabang...");
      startPolling(data.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal mengajukan refund"); } finally { setSubmitting(false); }
  };

  const startPolling = (rid) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/pos/refund-status/${rid}`);
        if (data.status === "approved") {
          setRefundStatus("approved");
          setApprovalInfo(data);
          toast.success(`Refund DISETUJUI oleh ${data.approved_by}!`);
          clearInterval(pollRef.current); pollRef.current = null;
        } else if (data.status === "rejected") {
          setRefundStatus("rejected");
          setApprovalInfo(data);
          toast.error(`Refund DITOLAK oleh ${data.approved_by}`);
          clearInterval(pollRef.current); pollRef.current = null;
        }
      } catch {}
    }, 3000);
  };

  const resetAll = () => {
    setCode(""); setTxData(null); setError(""); setReason("");
    setRefundId(null); setRefundStatus(null); setApprovalInfo(null);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  return (
    <div className="p-4 space-y-4" data-testid="refund-pos-panel">
      <div className="bg-[#0F0F0F] border border-[#262626] p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RotateCcw size={16} className="text-[#E60000]" />
            <h3 className="text-white text-sm font-bold uppercase tracking-wider">Refund POS</h3>
          </div>
          {(txData || refundId) && <button onClick={resetAll} className="text-xs text-gray-500 hover:text-red-400">Reset</button>}
        </div>
        <p className="text-gray-500 text-xs">Scan QR struk atau input kode transaksi. Garansi refund maksimal <span className="text-white font-bold">24 jam</span> setelah transaksi.</p>

        {/* Step 1: Input / Scan */}
        {!txData && !refundId && (
          <>
            <div className="flex gap-2">
              <Input className="bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none flex-1 font-mono" placeholder="Kode transaksi (FI-xxx)..." value={code} onChange={e => setCode(e.target.value)} onKeyDown={e => e.key === "Enter" && checkTx()} data-testid="refund-tx-input" />
              <Button onClick={() => checkTx()} disabled={checking} className="bg-[#E60000] hover:bg-[#FF1A1A] text-white h-10 rounded-none px-4" data-testid="refund-check-btn">
                {checking ? <Loader2 size={14} className="animate-spin" /> : "Cek"}
              </Button>
              <Button onClick={() => setScanning(!scanning)} className={`h-10 rounded-none px-3 ${scanning ? "bg-[#E60000] text-white" : "bg-[#262626] hover:bg-[#333] text-white"}`} data-testid="refund-camera-btn"><Camera size={16} /></Button>
            </div>
            {scanning && <BarcodeScanner onScan={handleScan} onClose={() => setScanning(false)} />}
            {error && <div className="border border-red-800/40 bg-red-900/10 px-3 py-2.5 text-red-400 text-xs flex items-center gap-2"><XCircle size={14} /> {error}</div>}
          </>
        )}

        {/* Step 2: Transaction found — show detail + reason */}
        {txData && !refundId && (
          <div className="space-y-3">
            <div className="border border-green-800/40 bg-green-900/10 p-3 space-y-2">
              <div className="flex items-center gap-2 text-green-400"><CheckCircle size={14} /><span className="text-xs font-bold">Transaksi Valid</span></div>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between"><span className="text-gray-500">Kode</span><span className="text-white font-mono">{txData.transaction_code}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Pelanggan</span><span className="text-white">{txData.customer_name}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Kasir</span><span className="text-white">{txData.cashier_name}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Metode</span><span className="text-white uppercase">{txData.payment_method}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Waktu</span><span className="text-white">{new Date(txData.created_at).toLocaleString("id-ID")}</span></div>
                {txData.items?.map((item, i) => (
                  <div key={i} className="flex justify-between text-gray-400"><span>{item.name} x{item.qty}</span><span>{formatPrice(item.price * item.qty)}</span></div>
                ))}
                <div className="flex justify-between font-bold border-t border-[#262626] pt-1 mt-1"><span className="text-white">Total Refund</span><span className="text-[#E60000]">{formatPrice(txData.total)}</span></div>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-yellow-900/20 border border-yellow-800/30 px-3 py-2 text-yellow-400 text-xs"><Clock size={12} /> Sisa garansi: {hoursLeft} jam</div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Alasan Refund</label>
              <Input className="bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none mt-1" placeholder="Jelaskan alasan refund..." value={reason} onChange={e => setReason(e.target.value)} data-testid="refund-reason-input" />
            </div>
            <Button onClick={submitRefund} disabled={submitting || !reason.trim()} className="w-full bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase tracking-wider h-11 rounded-none disabled:opacity-40" data-testid="refund-submit-btn">
              {submitting ? "Mengirim..." : <><RotateCcw size={14} className="mr-2" /> Ajukan Refund — {formatPrice(txData.total)}</>}
            </Button>
          </div>
        )}

        {/* Step 3: Waiting for approval */}
        {refundId && refundStatus === "pending" && (
          <div className="space-y-3">
            <div className="border border-yellow-800/40 bg-yellow-900/10 p-4 flex flex-col items-center gap-3">
              <Loader2 size={24} className="text-yellow-400 animate-spin" />
              <p className="text-yellow-400 text-sm font-bold">Menunggu Approval Kepala Cabang...</p>
              <p className="text-gray-500 text-xs text-center">Pengajuan refund {formatPrice(txData?.total || 0)} telah dikirim. Halaman ini otomatis update saat ada keputusan.</p>
            </div>
          </div>
        )}

        {/* Step 4: Approved */}
        {refundStatus === "approved" && (
          <div className="space-y-3">
            <div className="border border-green-800/40 bg-green-900/10 p-4 flex flex-col items-center gap-3">
              <CheckCircle size={28} className="text-green-400" />
              <p className="text-green-400 text-lg font-bold">Refund Disetujui!</p>
              <p className="text-white text-sm">Disetujui oleh: <span className="font-bold">{approvalInfo?.approved_by}</span> ({approvalInfo?.approved_role === "kepala_cabang" ? "Kepala Cabang" : approvalInfo?.approved_role})</p>
              <p className="text-gray-400 text-xs text-center">Kembalikan {formatPrice(txData?.total || 0)} dari kas kasir kepada pelanggan. Jumlah ini otomatis dipotong dari pendapatan harian.</p>
            </div>
            <Button onClick={resetAll} className="w-full bg-[#262626] hover:bg-[#333] text-white font-bold uppercase h-10 rounded-none">Selesai</Button>
          </div>
        )}

        {/* Step 4: Rejected */}
        {refundStatus === "rejected" && (
          <div className="space-y-3">
            <div className="border border-red-800/40 bg-red-900/10 p-4 flex flex-col items-center gap-3">
              <XCircle size={28} className="text-red-400" />
              <p className="text-red-400 text-lg font-bold">Refund Ditolak</p>
              <p className="text-white text-sm">Ditolak oleh: <span className="font-bold">{approvalInfo?.approved_by}</span></p>
              {approvalInfo?.reject_reason && <p className="text-gray-400 text-xs text-center">Alasan: {approvalInfo.reject_reason}</p>}
            </div>
            <Button onClick={resetAll} className="w-full bg-[#262626] hover:bg-[#333] text-white font-bold uppercase h-10 rounded-none">Kembali</Button>
          </div>
        )}
      </div>
    </div>
  );
}

// =================== PRINT ===================
// RawBT: Hidden iframe + window.print() — RawBT intercepts as Android Print Service
// Klik cetak → langsung dialog printer → pilih RawBT → cetak ke thermal bluetooth

function printViaRawBT(html) {
  let iframe = document.getElementById("rawbt-print-frame");
  if (iframe) iframe.remove();
  iframe = document.createElement("iframe");
  iframe.id = "rawbt-print-frame";
  iframe.style.cssText = "position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:none;opacity:0";
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch(e) {}
    setTimeout(() => { if (iframe.parentNode) iframe.remove(); }, 5000);
  }, 500);
}

// =================== MAIN POS PAGE ===================
export default function POSPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const receiptRef = useRef(null);
  const pollRef = useRef(null);

  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [capsters, setCapsters] = useState([]);
  const [cart, setCart] = useState([]);
  const [selectedCapster, setSelectedCapster] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [posMode, setPosMode] = useState("transaksi");
  const [printerOpen, setPrinterOpen] = useState(false);
  const [printerConfig, setPrinterConfig] = useState(loadPrinterConfig);
  const [cartOpen, setCartOpen] = useState(false);

  // Customer
  const [customerPhone, setCustomerPhone] = useState("");
  const [customer, setCustomer] = useState(null);
  const [loyaltyInfo, setLoyaltyInfo] = useState(null);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [quickName, setQuickName] = useState("");
  const [showQuickReg, setShowQuickReg] = useState(false);
  const [activeTab, setActiveTab] = useState("jasa");

  // Cash
  const [cashReceived, setCashReceived] = useState("");

  // EDC
  const [edcConfig, setEdcConfig] = useState(null);
  const [edcCardLast4, setEdcCardLast4] = useState("");
  const [edcApprovalCode, setEdcApprovalCode] = useState("");

  // QRIS
  const [qrisLoading, setQrisLoading] = useState(false);
  const [qrisPaymentId, setQrisPaymentId] = useState(null);
  const [qrisPosOrderId, setQrisPosOrderId] = useState(null);
  const [qrisStatus, setQrisStatus] = useState(null);
  const [qrisQrUrl, setQrisQrUrl] = useState(null);

  // Receipt
  const [showReceipt, setShowReceipt] = useState(false);
  const [lastTransaction, setLastTransaction] = useState(null);
  const [processing, setProcessing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [sRes, pRes, cRes] = await Promise.all([api.get("/services"), api.get("/products"), api.get("/capsters")]);
      setServices(sRes.data); setProducts(pRes.data.filter(p => p.is_active && p.stock > 0)); setCapsters(cRes.data);
    } catch {}
    try { const { data } = await api.get("/settings/edc-config"); setEdcConfig(data); } catch {}
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, []);

  const refreshPrinterConfig = () => setPrinterConfig(loadPrinterConfig());

  // Live search dropdown
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);

  // Debounce live search by name or phone (>= 2 chars)
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const { data } = await api.get(`/customers`, { params: { q } });
        setSearchResults((data || []).slice(0, 8));
      } catch { setSearchResults([]); }
      setSearchLoading(false);
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const pickCustomer = async (c) => {
    setShowSearchDropdown(false);
    setSearchQuery("");
    setCustomerPhone(c.phone || "");
    try {
      const { data } = await api.get(`/customers/search?phone=${c.phone}`);
      setCustomer(data.customer); setLoyaltyInfo(data); setShowQuickReg(false);
      toast.success(`Pelanggan dipilih: ${data.customer.name}`);
    } catch {
      // fallback if loyalty endpoint fails
      setCustomer(c); setLoyaltyInfo({ customer: c, coupons: [], visit_count: c.visit_count || 0, visits_until_next: 5 });
      setShowQuickReg(false);
    }
  };

  const searchCustomer = async () => {
    if (!customerPhone.trim()) return;
    try {
      const { data } = await api.get(`/customers/search?phone=${customerPhone}`);
      setCustomer(data.customer); setLoyaltyInfo(data); setShowQuickReg(false);
      toast.success(`Pelanggan ditemukan: ${data.customer.name}`);
    } catch { setCustomer(null); setLoyaltyInfo(null); setShowQuickReg(true); toast.info("Pelanggan tidak ditemukan. Daftarkan?"); }
  };

  const quickRegister = async () => {
    if (!quickName.trim() || !customerPhone.trim()) return;
    try {
      const { data } = await api.post("/customers/quick-register", { name: quickName, phone: customerPhone });
      setCustomer(data); setLoyaltyInfo({ customer: data, coupons: [], visit_count: 0, visits_until_next: 5 }); setShowQuickReg(false);
      toast.success(`Pelanggan baru: ${data.name}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const addToCart = (item, type) => {
    const existing = cart.findIndex(i => i.id === item.id);
    if (existing >= 0) { const c = [...cart]; c[existing].qty += 1; setCart(c); }
    else setCart([...cart, { ...item, qty: 1, item_type: type }]);
    resetQris();
  };

  const updateQty = (idx, delta) => { const c = [...cart]; c[idx].qty += delta; if (c[idx].qty <= 0) c.splice(idx, 1); setCart(c); resetQris(); };
  const removeItem = (idx) => { const c = [...cart]; c.splice(idx, 1); setCart(c); resetQris(); };
  const resetQris = () => { setQrisPaymentId(null); setQrisPosOrderId(null); setQrisStatus(null); setQrisQrUrl(null); if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const discount = selectedCoupon ? (cart.find(i => i.category === "potong")?.price || 0) : 0;
  const total = Math.max(0, subtotal - discount);
  const cashReceivedNum = parseInt(cashReceived) || 0;
  const cashChange = cashReceivedNum - total;

  const handleGetQris = async () => {
    if (cart.length === 0) return toast.error("Keranjang kosong");
    setQrisLoading(true);
    try {
      const { data } = await api.post("/pos/create-qris", {
        amount: total,
        customer_name: customer?.name || "Walk-in",
        customer_phone: customer?.phone || customerPhone || ""
      });
      setQrisPaymentId(data.payment_id);
      setQrisPosOrderId(data.pos_order_id);
      setQrisQrUrl(data.qr_url);
      setQrisStatus("pending");
      toast.success("QRIS berhasil dibuat. Silakan scan!");
      startPolling(data.payment_id);
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal membuat QRIS"); } finally { setQrisLoading(false); }
  };

  const startPolling = (paymentId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const { data } = await api.get(`/pos/qris-status/${paymentId}`);
        if (data.status === "paid") { setQrisStatus("paid"); toast.success("QRIS berhasil!"); clearInterval(pollRef.current); pollRef.current = null; }
        else if (data.status === "failed" || data.status === "expired") { setQrisStatus("failed"); clearInterval(pollRef.current); pollRef.current = null; }
      } catch {}
      if (attempts > 120) { clearInterval(pollRef.current); pollRef.current = null; }
    }, 2000);
  };

  const checkout = async () => {
    if (cart.length === 0) return toast.error("Keranjang kosong");
    if (paymentMethod === "qris" && qrisStatus !== "paid") return toast.error("Selesaikan QRIS terlebih dahulu");
    if (paymentMethod === "cash" && cashReceivedNum < total) return toast.error("Uang diterima kurang");
    setProcessing(true);
    try {
      const payload = {
        customer_id: customer?.id || null, customer_name: customer?.name || "Walk-in",
        customer_phone: customer?.phone || customerPhone || "",
        items: cart.map(i => ({ id: i.id, name: i.name, price: i.price, qty: i.qty, category: i.category || "", item_type: i.item_type || "service" })),
        payment_method: paymentMethod, capster_id: selectedCapster,
        loyalty_coupon_id: selectedCoupon?.id || null,
        qris_order_id: paymentMethod === "qris" ? qrisPaymentId : null,
        cash_received: paymentMethod === "cash" ? cashReceivedNum : null,
        cash_change: paymentMethod === "cash" ? Math.max(0, cashChange) : null,
        edc_trace_number: paymentMethod === "edc" ? edcApprovalCode.trim() : null,
        edc_card_last4: paymentMethod === "edc" ? edcCardLast4 : null,
        edc_approval_code: paymentMethod === "edc" ? edcApprovalCode.trim() : null,
      };
      const { data } = await api.post("/transactions", payload);
      setLastTransaction({ ...data, cash_received: paymentMethod === "cash" ? cashReceivedNum : 0, cash_change: paymentMethod === "cash" ? Math.max(0, cashChange) : 0, qris_pos_order_id: qrisPosOrderId || "", edc_card_last4: paymentMethod === "edc" ? edcCardLast4 : "", edc_approval_code: paymentMethod === "edc" ? edcApprovalCode.trim() : "" });
      setShowReceipt(true); setCart([]); setCustomer(null); setLoyaltyInfo(null); setSelectedCoupon(null); setCustomerPhone(""); setCashReceived(""); setEdcCardLast4(""); setEdcApprovalCode(""); resetQris();
      if (data.new_coupon) toast.success(`Kupon loyalitas baru: ${data.new_coupon}`);
      toast.success("Transaksi berhasil!");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setProcessing(false); }
  };

  const handleQueueDone = async (queueCustomer) => {
    // Switch to transaction mode with customer prefilled
    setPosMode("transaksi");
    if (queueCustomer.customer_phone) {
      setCustomerPhone(queueCustomer.customer_phone);
      // Auto-search customer
      try {
        const { data } = await api.get(`/customers/search?phone=${queueCustomer.customer_phone}`);
        setCustomer(data.customer);
        setLoyaltyInfo(data);
        toast.info(`Pelanggan ${data.customer.name} dimuat dari antrian`);
      } catch {
        setCustomerPhone(queueCustomer.customer_phone);
      }
    }
    if (queueCustomer.customer_name && !customer) {
      setQuickName(queueCustomer.customer_name);
    }
  };

  const handleRedeemSuccess = (txData) => { setLastTransaction(txData); setShowReceipt(true); };

  const handlePrint = () => {
    const t = lastTransaction;
    if (!t) return;
    const pw = printerConfig.paperSize === "80" ? "80mm" : "58mm";
    const fs = printerConfig.paperSize === "80" ? "11px" : "10px";

    const itemsHtml = (t.items || []).map(it => `<div style="display:flex;justify-content:space-between;margin-bottom:1px"><span>${it.name} x${it.qty}</span><span>${formatPrice(it.price * it.qty)}</span></div>`).join("");

    let paymentHtml = "";
    if (t.payment_method === "cash") {
      paymentHtml = `<div style="display:flex;justify-content:space-between"><span>Bayar (Tunai)</span><span>${formatPrice(t.cash_received)}</span></div><div style="display:flex;justify-content:space-between;font-weight:bold"><span>Kembalian</span><span>${formatPrice(t.cash_change)}</span></div>`;
    } else if (t.payment_method === "kupon") {
      paymentHtml = `<div style="display:flex;justify-content:space-between;font-weight:bold"><span>Bayar (Kupon)</span><span>GRATIS</span></div>${t.redeem_coupon_code ? `<div style="display:flex;justify-content:space-between;font-size:8px"><span>Kode Kupon</span><span>${t.redeem_coupon_code}</span></div>` : ""}`;
    } else if (t.payment_method === "edc") {
      paymentHtml = `<div style="display:flex;justify-content:space-between"><span>Bayar (EDC)</span><span>${formatPrice(t.total)}</span></div>${t.edc_card_last4 ? `<div style="display:flex;justify-content:space-between;font-size:8px"><span>Kartu</span><span>****${t.edc_card_last4}</span></div>` : ""}${t.edc_approval_code ? `<div style="display:flex;justify-content:space-between;font-size:8px"><span>Approval</span><span>${t.edc_approval_code}</span></div>` : ""}`;
    } else {
      paymentHtml = `<div style="display:flex;justify-content:space-between"><span>Bayar (QRIS)</span><span>${formatPrice(t.total)}</span></div>${t.qris_pos_order_id ? `<div style="display:flex;justify-content:space-between;font-size:8px"><span>Ref</span><span>${t.qris_pos_order_id}</span></div>` : ""}`;
    }

    const qrValue = t.new_coupon || t.transaction_code;
    const qrLabel = t.new_coupon ? `<p style="font-weight:bold;font-size:9px">KUPON LOYALITAS</p><p style="font-size:7px;margin-bottom:4px">Kumpulkan 5 kupon = 1x potong gratis!</p>` : "";
    const qrFooterLabel = t.new_coupon ? t.new_coupon : t.transaction_code;

    // Use QRCodeSVG from react to get SVG string
    const qrContainer = document.createElement("div");
    const root = document.createElement("div");
    qrContainer.appendChild(root);
    // Render QR as inline SVG via the receipt modal's existing QR
    let qrSvgHtml = "";
    const existingQr = document.querySelector("#receipt-print svg");
    if (existingQr) {
      qrSvgHtml = existingQr.outerHTML;
    }

    const html = `<!DOCTYPE html><html><head><title>Struk ${t.transaction_code}</title>
<style>@page{size:${pw} auto;margin:0}*{margin:0;padding:0;box-sizing:border-box}body{font-family:monospace;font-size:${fs};color:#000;background:#fff;padding:2mm;width:${pw};max-width:${pw}}.sep{border-bottom:1px dashed #000;margin:4px 0}</style>
</head><body>
<div style="text-align:center;margin-bottom:6px">
<p style="font-weight:bold;font-size:14px">FI BARBERSHOP SEKIP</p>
<p style="font-size:8px">Jl. Ampibi, 20 Ilir D II, Kec. Kemuning</p>
<p style="font-size:8px">Kota Palembang, Sumatera Selatan</p>
</div>
<div class="sep"></div>
<div style="margin-bottom:4px">
<p>No: ${t.transaction_code}</p>
<p>Tgl: ${new Date(t.created_at).toLocaleString("id-ID")}</p>
<p>Kasir: ${t.cashier_name}</p>
<p>Pelanggan: ${t.customer_name}</p>
</div>
<div class="sep"></div>
${itemsHtml}
<div class="sep"></div>
<div style="display:flex;justify-content:space-between"><span>Subtotal</span><span>${formatPrice(t.subtotal)}</span></div>
${t.discount > 0 ? `<div style="display:flex;justify-content:space-between;color:green"><span>Diskon</span><span>-${formatPrice(t.discount)}</span></div>` : ""}
<div style="display:flex;justify-content:space-between;font-weight:bold;font-size:12px;margin-top:2px"><span>TOTAL</span><span>${formatPrice(t.total)}</span></div>
<div class="sep"></div>
${paymentHtml}
<div class="sep" style="margin-top:6px"></div>
<div style="text-align:center;margin-top:4px">
${qrLabel}
${qrSvgHtml}
<p style="font-size:8px;margin-top:2px">${qrFooterLabel}</p>
</div>
<div style="text-align:center;margin-top:6px">
<p style="font-size:8px">Terima kasih atas kunjungan Anda!</p>
<p style="font-size:8px">Kumpulkan 5 kupon layanan = 1x potong GRATIS</p>
</div>
</body></html>`;
    printViaRawBT(html);
  };

  const canCheckout = () => {
    if (cart.length === 0) return false;
    if (paymentMethod === "qris" && qrisStatus !== "paid") return false;
    if (paymentMethod === "cash" && cashReceivedNum < total) return false;
    if (paymentMethod === "edc" && (edcCardLast4.length !== 4 || !edcApprovalCode.trim())) return false;
    return true;
  };

  return (
    <>
      <div className="flex h-screen bg-[#050505] overflow-hidden" data-testid="pos-page">
        {/* Left: Catalog */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          {/* Header */}
          <div className="bg-[#0F0F0F] border-b border-[#262626] shrink-0">
            <div className="h-14 flex items-center justify-between px-3 md:px-4 gap-2">
              <div className="flex items-center gap-2 md:gap-3 min-w-0">
                <button onClick={() => navigate("/admin")} className="text-gray-400 hover:text-white shrink-0" data-testid="pos-back-btn"><ArrowLeft size={18} /></button>
                <img src={LOGO} alt="FI" className="w-7 h-7 rounded-full shrink-0" />
                <span className="font-heading text-sm font-bold uppercase tracking-wider text-white hidden sm:inline">POS KASIR</span>
              </div>
              {/* Mode Toggle (desktop) */}
              <div className="hidden lg:flex items-center gap-1 bg-[#0A0A0A] border border-[#262626] p-0.5">
                <button onClick={() => setPosMode("transaksi")} className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${posMode === "transaksi" ? "bg-[#E60000] text-white" : "text-gray-500 hover:text-gray-300"}`} data-testid="mode-transaksi">Transaksi</button>
                <button onClick={() => setPosMode("redeem")} className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-1.5 ${posMode === "redeem" ? "bg-[#E60000] text-white" : "text-gray-500 hover:text-gray-300"}`} data-testid="mode-redeem"><Gift size={12} /> Redeem</button>
                <button onClick={() => setPosMode("antrian")} className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-1.5 ${posMode === "antrian" ? "bg-[#E60000] text-white" : "text-gray-500 hover:text-gray-300"}`} data-testid="mode-antrian"><Users size={12} /> Antrian</button>
                <button onClick={() => setPosMode("refund")} className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-1.5 ${posMode === "refund" ? "bg-[#E60000] text-white" : "text-gray-500 hover:text-gray-300"}`} data-testid="mode-refund"><RotateCcw size={12} /> Refund</button>
              </div>
              <div className="flex items-center gap-1.5 md:gap-2">
                <button onClick={() => { setPrinterOpen(true); }} className="text-gray-400 hover:text-[#E60000] p-1.5 bg-[#0A0A0A] border border-[#262626] transition-colors" data-testid="printer-settings-btn" title="Pengaturan Printer">
                  <Settings size={16} />
                </button>
                <span className="text-xs text-gray-400 hidden md:inline truncate max-w-[120px]">{user?.name}</span>
                <button onClick={async () => { await logout(); navigate("/login"); }} className="text-xs text-gray-500 hover:text-[#E60000] whitespace-nowrap" data-testid="pos-logout">Keluar</button>
              </div>
            </div>
            {/* Mode Toggle (mobile/tablet) - horizontally scrollable */}
            <div className="lg:hidden flex items-center gap-1 bg-[#0A0A0A] border-t border-[#262626] p-1 overflow-x-auto" style={{ scrollbarWidth: "none" }} data-testid="mode-toggle-mobile">
              <button onClick={() => setPosMode("transaksi")} className={`shrink-0 px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${posMode === "transaksi" ? "bg-[#E60000] text-white" : "text-gray-500 hover:text-gray-300"}`} data-testid="mode-transaksi-m">Transaksi</button>
              <button onClick={() => setPosMode("redeem")} className={`shrink-0 px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-1.5 ${posMode === "redeem" ? "bg-[#E60000] text-white" : "text-gray-500 hover:text-gray-300"}`} data-testid="mode-redeem-m"><Gift size={12} /> Redeem</button>
              <button onClick={() => setPosMode("antrian")} className={`shrink-0 px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-1.5 ${posMode === "antrian" ? "bg-[#E60000] text-white" : "text-gray-500 hover:text-gray-300"}`} data-testid="mode-antrian-m"><Users size={12} /> Antrian</button>
              <button onClick={() => setPosMode("refund")} className={`shrink-0 px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-1.5 ${posMode === "refund" ? "bg-[#E60000] text-white" : "text-gray-500 hover:text-gray-300"}`} data-testid="mode-refund-m"><RotateCcw size={12} /> Refund</button>
            </div>
          </div>

          {posMode === "redeem" ? (
            <div className="flex-1 overflow-auto"><RedeemPanel onRedeemSuccess={handleRedeemSuccess} /></div>
          ) : posMode === "antrian" ? (
            <div className="flex-1 overflow-auto"><QueuePanel onQueueDone={handleQueueDone} /></div>
          ) : posMode === "refund" ? (
            <div className="flex-1 overflow-auto"><RefundPOSPanel /></div>
          ) : (
            <>
              {/* Customer Search */}
              <div className="p-4 bg-[#0A0A0A] border-b border-[#262626] shrink-0">
                {/* Live Search (name or phone) */}
                <div className="relative">
                  <div className="flex gap-2 items-center">
                    <div className="relative flex-1">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                      <Input
                        data-testid="customer-livesearch-input"
                        className="bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none pl-9"
                        placeholder="Cari pelanggan: nama atau No. HP..."
                        value={searchQuery}
                        onChange={e => { setSearchQuery(e.target.value); setShowSearchDropdown(true); }}
                        onFocus={() => setShowSearchDropdown(true)}
                        onBlur={() => setTimeout(() => setShowSearchDropdown(false), 200)}
                      />
                      {searchLoading && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 animate-spin" />}
                    </div>
                  </div>
                  {/* Dropdown results */}
                  {showSearchDropdown && searchQuery.trim().length >= 2 && (
                    <div className="absolute left-0 right-0 mt-1 bg-[#0F0F0F] border border-[#262626] shadow-2xl z-30 max-h-72 overflow-auto" data-testid="search-results-dropdown">
                      {searchResults.length === 0 && !searchLoading ? (
                        <div className="p-4 text-xs text-gray-500 text-center">
                          <p>Tidak ditemukan</p>
                          <button
                            onClick={() => { setCustomerPhone(searchQuery.replace(/\D/g, "")); setQuickName(/^\d+$/.test(searchQuery) ? "" : searchQuery); setShowQuickReg(true); setShowSearchDropdown(false); setSearchQuery(""); }}
                            className="mt-2 text-[#E60000] font-bold uppercase tracking-wider text-[10px] hover:underline"
                            data-testid="register-new-from-search"
                          >+ Daftarkan Baru</button>
                        </div>
                      ) : searchResults.map((c) => (
                        <button
                          key={c.id}
                          onMouseDown={(e) => { e.preventDefault(); pickCustomer(c); }}
                          className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-[#1A1A1A] border-b border-[#1A1A1A] last:border-b-0 transition-colors"
                          data-testid={`search-result-${c.id}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <User size={14} className="text-[#E60000] shrink-0" />
                            <div className="min-w-0">
                              <p className="text-white text-sm font-semibold truncate">{c.name}</p>
                              <p className="text-gray-500 text-xs truncate">{c.phone || "-"}</p>
                            </div>
                          </div>
                          <Badge className="text-[9px] bg-[#262626] shrink-0">{(c.user_type || "silver").toUpperCase()}</Badge>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {customer && (
                  <div className="mt-3 flex items-center justify-between bg-[#0F0F0F] border border-[#262626] p-3">
                    <div className="flex items-center gap-3">
                      <User size={16} className="text-[#E60000]" />
                      <div>
                        <p className="text-white text-sm font-semibold">{customer.name}</p>
                        <p className="text-gray-500 text-xs">{customer.phone} | <Badge className="text-[10px] bg-[#262626]">{customer.user_type?.toUpperCase()}</Badge></p>
                      </div>
                    </div>
                    {loyaltyInfo && (
                      <div className="text-right">
                        <p className="text-xs text-gray-400">Kunjungan: <span className="text-white font-bold">{loyaltyInfo.visit_count}</span></p>
                        <p className="text-xs text-gray-400">{loyaltyInfo.visits_until_next} lagi untuk kupon gratis</p>
                      </div>
                    )}
                  </div>
                )}
                {loyaltyInfo?.coupons?.length > 0 && (
                  <div className="mt-2 flex gap-2 flex-wrap">
                    {loyaltyInfo.coupons.map(c => (
                      <button key={c.id} onClick={() => setSelectedCoupon(selectedCoupon?.id === c.id ? null : c)} className={`text-xs px-3 py-1.5 border transition-colors flex items-center gap-1 ${selectedCoupon?.id === c.id ? "border-[#E60000] bg-[#E60000]/10 text-[#E60000]" : "border-[#262626] text-gray-400 hover:border-[#E60000]"}`} data-testid={`coupon-${c.id}`}><Gift size={12} /> {c.coupon_code}</button>
                    ))}
                  </div>
                )}
                {showQuickReg && (
                  <div className="mt-3 flex flex-col sm:flex-row gap-2" data-testid="quick-register-form">
                    <Input className="bg-[#1A1A1A] border-[#262626] text-white h-9 rounded-none flex-1" placeholder="Nama pelanggan baru..." value={quickName} onChange={e => setQuickName(e.target.value)} data-testid="quick-register-name" />
                    <Input className="bg-[#1A1A1A] border-[#262626] text-white h-9 rounded-none flex-1 font-mono" placeholder="No. HP..." value={customerPhone} onChange={e => setCustomerPhone(e.target.value.replace(/\D/g, ""))} data-testid="quick-register-phone" />
                    <Button onClick={quickRegister} className="bg-[#E60000] hover:bg-[#FF1A1A] text-white h-9 rounded-none text-xs" data-testid="quick-register-btn">Daftarkan</Button>
                  </div>
                )}
              </div>
              {/* Tab: Jasa / Produk */}
              <div className="flex border-b border-[#262626] bg-[#0A0A0A] shrink-0">
                <button onClick={() => setActiveTab("jasa")} className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wider text-center transition-colors ${activeTab === "jasa" ? "text-[#E60000] border-b-2 border-[#E60000] bg-[#E60000]/5" : "text-gray-500 hover:text-gray-300"}`} data-testid="tab-jasa">Layanan / Jasa</button>
                <button onClick={() => setActiveTab("produk")} className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wider text-center transition-colors ${activeTab === "produk" ? "text-[#E60000] border-b-2 border-[#E60000] bg-[#E60000]/5" : "text-gray-500 hover:text-gray-300"}`} data-testid="tab-produk">Produk</button>
              </div>
              {/* Item Grid */}
              <div className="flex-1 overflow-auto p-4 pb-28 lg:pb-4">
                {activeTab === "jasa" ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="service-catalog">
                    {services.map(s => (
                      <button key={s.id} onClick={() => addToCart(s, "service")} className="bg-[#0F0F0F] border border-[#262626] p-4 text-left hover:border-[#E60000] hover:-translate-y-0.5 transition-all duration-200 group" data-testid={`pos-service-${s.id}`}>
                        <Badge className="text-[9px] bg-[#262626] text-gray-400 mb-2">{s.category}</Badge>
                        <p className="text-white text-sm font-semibold mb-1 group-hover:text-[#E60000] transition-colors">{s.name}</p>
                        <p className="text-[#E60000] font-heading text-lg font-bold">{formatPrice(s.price)}</p>
                      </button>
                    ))}
                    {services.length === 0 && <p className="text-gray-500 text-sm col-span-full text-center py-8">Tidak ada layanan</p>}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="product-catalog">
                    {products.map(p => (
                      <button key={p.id} onClick={() => addToCart(p, "product")} className="bg-[#0F0F0F] border border-[#262626] p-4 text-left hover:border-[#E60000] hover:-translate-y-0.5 transition-all duration-200 group" data-testid={`pos-product-${p.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <Badge className="text-[9px] bg-[#262626] text-gray-400">{p.category}</Badge>
                          <span className="text-[10px] text-gray-500">Stok: {p.stock}</span>
                        </div>
                        <p className="text-white text-sm font-semibold mb-1 group-hover:text-[#E60000] transition-colors">{p.name}</p>
                        <p className="text-[#E60000] font-heading text-lg font-bold">{formatPrice(p.price)}</p>
                      </button>
                    ))}
                    {products.length === 0 && <p className="text-gray-500 text-sm col-span-full text-center py-8">Tidak ada produk</p>}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Right: Cart - overlay drawer on mobile, static on desktop */}
        {posMode === "transaksi" && (
          <>
            {/* Floating Cart Indicator (mobile only) */}
            {!cartOpen && (
              <button
                onClick={() => setCartOpen(true)}
                className="lg:hidden fixed bottom-4 left-4 right-4 z-30 bg-[#E60000] hover:bg-[#FF1A1A] text-white shadow-2xl shadow-[#E60000]/40 flex items-center justify-between gap-3 px-5 py-3 rounded-full border-2 border-[#FF1A1A]/40 active:scale-[0.98] transition-all duration-200"
                data-testid="floating-cart-btn"
                aria-label="Buka keranjang"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="relative shrink-0">
                    <ShoppingCart size={20} />
                    {cart.length > 0 && (
                      <span className="absolute -top-2 -right-2 bg-white text-[#E60000] text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1" data-testid="floating-cart-count">
                        {cart.length}
                      </span>
                    )}
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider whitespace-nowrap">
                    {cart.length === 0 ? "Keranjang Kosong" : `${cart.length} Item`}
                  </span>
                </div>
                <span className="font-heading text-base font-bold whitespace-nowrap" data-testid="floating-cart-total">
                  {formatPrice(total)}
                </span>
              </button>
            )}
            {cartOpen && (
              <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
                onClick={() => setCartOpen(false)}
                data-testid="cart-backdrop"
              />
            )}
            <div
              className={`fixed lg:static top-0 right-0 h-full w-full sm:max-w-sm lg:w-80 xl:w-96 bg-[#0F0F0F] border-l border-[#262626] flex flex-col shrink-0 z-50 transform transition-transform duration-300 ease-in-out ${
                cartOpen ? "translate-x-0" : "translate-x-full"
              } lg:translate-x-0`}
              data-testid="pos-cart"
            >
            <div className="p-4 border-b border-[#262626] flex items-center justify-between">
              <div>
                <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-white">Keranjang</h2>
                <p className="text-xs text-gray-500">{cart.length} item</p>
              </div>
              <button onClick={() => setCartOpen(false)} className="lg:hidden text-gray-400 hover:text-white p-1" data-testid="cart-close-btn" aria-label="Tutup keranjang">
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-2">
              {cart.length === 0 ? <p className="text-gray-500 text-sm text-center py-8">Keranjang kosong</p> : cart.map((item, idx) => (
                <div key={idx} className="bg-[#050505] border border-[#262626] p-3 flex items-center justify-between" data-testid={`cart-item-${idx}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-white text-sm font-semibold truncate">{item.name}</p>
                      <Badge className="text-[8px] bg-[#1A1A1A] text-gray-500 shrink-0">{item.item_type === "product" ? "Produk" : "Jasa"}</Badge>
                    </div>
                    <p className="text-[#E60000] text-xs">{formatPrice(item.price)}</p>
                  </div>
                  <div className="flex items-center gap-1.5 ml-3">
                    <button onClick={() => updateQty(idx, -1)} className="w-6 h-6 flex items-center justify-center bg-[#262626] text-white hover:bg-[#333]" data-testid={`cart-minus-${idx}`}><Minus size={12} /></button>
                    <span className="text-white text-sm w-6 text-center font-bold">{item.qty}</span>
                    <button onClick={() => updateQty(idx, 1)} className="w-6 h-6 flex items-center justify-center bg-[#262626] text-white hover:bg-[#333]" data-testid={`cart-plus-${idx}`}><Plus size={12} /></button>
                    <button onClick={() => removeItem(idx)} className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-[#E60000]" data-testid={`cart-remove-${idx}`}><Trash2 size={12} /></button>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-[#262626] space-y-3 bg-[#0A0A0A]">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Capster</label>
                <Select value={selectedCapster} onValueChange={setSelectedCapster}>
                  <SelectTrigger className="bg-[#1A1A1A] border-[#262626] text-white h-9 rounded-none text-xs" data-testid="capster-select"><SelectValue placeholder="Pilih capster..." /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#262626]">{capsters.map(c => <SelectItem key={c.id} value={c.id} className="text-white text-xs">{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Pembayaran</label>
                <Select value={paymentMethod} onValueChange={v => { setPaymentMethod(v); resetQris(); setCashReceived(""); setEdcCardLast4(""); setEdcApprovalCode(""); }}>
                  <SelectTrigger className="bg-[#1A1A1A] border-[#262626] text-white h-9 rounded-none text-xs" data-testid="payment-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                    <SelectItem value="cash" className="text-white text-xs"><span className="flex items-center gap-1.5"><Banknote size={12} /> Cash / Tunai</span></SelectItem>
                    <SelectItem value="qris" className="text-white text-xs"><span className="flex items-center gap-1.5"><QrCodeIcon size={12} /> QRIS</span></SelectItem>
                    {edcConfig?.enabled && <SelectItem value="edc" className="text-white text-xs"><span className="flex items-center gap-1.5"><CreditCard size={12} /> Debit/Kredit (EDC){edcConfig.bank_name ? ` - ${edcConfig.bank_name}` : ""}</span></SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              {paymentMethod === "cash" && cart.length > 0 && (
                <div className="space-y-2 pt-1">
                  <div><label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Uang Diterima</label>
                  <Input type="number" className="bg-[#1A1A1A] border-[#262626] text-white h-9 rounded-none text-sm font-mono" placeholder="Masukkan nominal..." value={cashReceived} onChange={e => setCashReceived(e.target.value)} data-testid="cash-received-input" /></div>
                  {cashReceivedNum > 0 && <div className={`flex justify-between text-sm px-1 ${cashChange >= 0 ? "text-green-400" : "text-red-400"}`}><span>Kembalian</span><span className="font-bold font-mono">{cashChange >= 0 ? formatPrice(cashChange) : `Kurang ${formatPrice(Math.abs(cashChange))}`}</span></div>}
                </div>
              )}
              {paymentMethod === "qris" && cart.length > 0 && (
                <div className="space-y-2 pt-1">
                  {!qrisStatus && <Button onClick={handleGetQris} disabled={qrisLoading} className="w-full bg-[#1A6CFF] hover:bg-[#1559D6] text-white font-bold uppercase tracking-wider h-10 rounded-none text-sm" data-testid="get-qris-btn">{qrisLoading ? <><Loader2 size={14} className="mr-2 animate-spin" /> Membuat QRIS...</> : <><QrCodeIcon size={14} className="mr-2" /> Get QRIS</>}</Button>}
                  {qrisStatus === "paid" && <div className="flex items-center gap-2 bg-green-900/20 border border-green-800/30 px-3 py-2.5 text-green-400 text-xs"><CheckCircle size={14} /><div><p className="font-bold">QRIS Berhasil Dibayar</p><p className="text-[10px] text-green-300/70 font-mono mt-0.5">REF: {qrisPosOrderId}</p></div></div>}
                  {qrisStatus === "failed" && <div className="space-y-2"><div className="flex items-center gap-2 bg-red-900/20 border border-red-800/30 px-3 py-2 text-red-400 text-xs"><X size={14} /><span>QRIS Gagal/Expired</span></div><Button onClick={resetQris} className="w-full bg-[#262626] hover:bg-[#333] text-white text-xs h-8 rounded-none">Coba Lagi</Button></div>}
                </div>
              )}
              {paymentMethod === "edc" && cart.length > 0 && (
                <div className="space-y-2 pt-1">
                  <div className="bg-[#111] border border-[#262626] px-3 py-2 text-[10px] text-gray-400">
                    <CreditCard size={12} className="inline mr-1 text-[#E60000]" />
                    Proses pembayaran di mesin EDC{edcConfig?.bank_name ? ` (${edcConfig.bank_name})` : ""}, lalu masukkan data dari struk mesin.
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">4 Digit Terakhir Kartu</label>
                    <Input className="bg-[#1A1A1A] border-[#262626] text-white h-9 rounded-none text-sm font-mono" placeholder="Contoh: 1234" maxLength={4} value={edcCardLast4} onChange={e => setEdcCardLast4(e.target.value.replace(/\D/g, "").slice(0, 4))} data-testid="edc-card-last4" />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Approval Code</label>
                    <Input className="bg-[#1A1A1A] border-[#262626] text-white h-9 rounded-none text-sm font-mono" placeholder="Masukkan approval code..." value={edcApprovalCode} onChange={e => setEdcApprovalCode(e.target.value)} data-testid="edc-approval-code" />
                  </div>
                  {edcCardLast4.length === 4 && edcApprovalCode.trim() && <div className="flex items-center gap-2 bg-green-900/20 border border-green-800/30 px-3 py-1.5 text-green-400 text-xs"><CheckCircle size={12} /><span>Kartu ****{edcCardLast4} | Approval: {edcApprovalCode}</span></div>}
                </div>
              )}
              <div className="space-y-1 pt-2 border-t border-[#262626]">
                <div className="flex justify-between text-sm"><span className="text-gray-400">Subtotal</span><span className="text-white">{formatPrice(subtotal)}</span></div>
                {discount > 0 && <div className="flex justify-between text-sm"><span className="text-green-400">Diskon Kupon</span><span className="text-green-400">-{formatPrice(discount)}</span></div>}
                <div className="flex justify-between text-lg font-bold pt-1"><span className="text-white font-heading uppercase">Total</span><span className="text-[#E60000] font-heading">{formatPrice(total)}</span></div>
              </div>
              <Button onClick={checkout} disabled={!canCheckout() || processing} className="w-full bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase tracking-wider h-12 rounded-none text-base disabled:opacity-40" data-testid="checkout-btn">{processing ? "Memproses..." : "Bayar"}</Button>
            </div>
          </div>
          </>
        )}

        {/* QRIS Popup Modal */}
        <Dialog open={qrisStatus === "pending" && !!qrisQrUrl} onOpenChange={(open) => { if (!open) resetQris(); }}>
          <DialogContent className="bg-[#0A0A0A] border-[#262626] text-white max-w-xs rounded-none p-0" data-testid="qris-modal">
            <div className="p-5 space-y-4">
              <div className="text-center">
                <QrCodeIcon size={20} className="text-[#1A6CFF] mx-auto mb-2" />
                <h3 className="font-heading text-lg font-bold uppercase">Scan QRIS</h3>
                <p className="text-gray-400 text-xs mt-1">Total: <span className="text-white font-bold">{formatPrice(total)}</span></p>
              </div>
              {/* Printable QRIS area */}
              <div id="qris-print" className="bg-white p-4 flex flex-col items-center rounded-sm">
                <p className="text-black font-bold text-sm mb-0.5">FI BARBERSHOP SEKIP</p>
                <p className="text-gray-500 text-[9px] mb-2">Pembayaran QRIS</p>
                <img src={qrisQrUrl} alt="QRIS" className="w-52 h-52 object-contain" data-testid="qris-image" />
                <p className="text-black font-bold text-lg mt-2">{formatPrice(total)}</p>
                <p className="text-gray-400 font-mono text-[9px]">{qrisPosOrderId}</p>
              </div>
              <div className="flex items-center justify-center gap-2 text-yellow-400 text-xs">
                <Loader2 size={14} className="animate-spin" />
                <span>Menunggu pembayaran...</span>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => {
                  const html = `<!DOCTYPE html><html><head><title>QRIS</title>
<style>@page{size:80mm auto;margin:0}*{margin:0;padding:0;box-sizing:border-box}body{font-family:monospace;text-align:center;padding:4mm;color:#000;background:#fff}img{display:block;margin:8px auto;max-width:60mm}</style>
</head><body>
<h2 style="font-size:14px;margin-bottom:2px">FI BARBERSHOP SEKIP</h2>
<p style="font-size:10px;color:#666;margin-bottom:8px">Pembayaran QRIS</p>
<img src="${qrisQrUrl}" style="width:55mm;height:55mm" />
<p style="font-size:18px;font-weight:bold;margin-top:8px">${formatPrice(total)}</p>
<p style="font-size:9px;color:#999;margin-top:4px">${qrisPosOrderId}</p>
<p style="font-size:8px;color:#999;margin-top:8px">Scan QR di atas untuk membayar</p>
</body></html>`;
                  printViaRawBT(html, "QRIS");
                }} className="flex-1 bg-[#262626] hover:bg-[#333] text-white font-bold uppercase text-xs h-9 rounded-none" data-testid="print-qris-btn"><Printer size={14} className="mr-1.5" /> Cetak QRIS</Button>
                <Button onClick={resetQris} variant="ghost" className="text-gray-500 text-xs h-9 px-3">Batal</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Receipt Modal */}
        <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
          <DialogContent className="bg-white text-black max-w-sm rounded-none p-0 border-0">
            <div id="receipt-print" ref={receiptRef} className="p-4 font-mono text-[10px] leading-tight" data-testid="receipt-modal">
              <div className="text-center mb-3">
                <p className="font-bold text-sm">FI BARBERSHOP SEKIP</p>
                <p className="text-[8px]">Jl. Ampibi, 20 Ilir D II, Kec. Kemuning</p>
                <p className="text-[8px]">Kota Palembang, Sumatera Selatan</p>
                <div className="border-b border-dashed border-black my-1.5" />
              </div>
              {lastTransaction && (
                <>
                  <div className="mb-2">
                    <p>No: {lastTransaction.transaction_code}</p>
                    <p>Tgl: {new Date(lastTransaction.created_at).toLocaleString("id-ID")}</p>
                    <p>Kasir: {lastTransaction.cashier_name}</p>
                    <p>Pelanggan: {lastTransaction.customer_name}</p>
                  </div>
                  <div className="border-b border-dashed border-black my-1.5" />
                  {lastTransaction.items?.map((item, i) => (
                    <div key={i} className="flex justify-between mb-0.5"><span>{item.name} x{item.qty}</span><span>{formatPrice(item.price * item.qty)}</span></div>
                  ))}
                  <div className="border-b border-dashed border-black my-1.5" />
                  <div className="flex justify-between"><span>Subtotal</span><span>{formatPrice(lastTransaction.subtotal)}</span></div>
                  {lastTransaction.discount > 0 && <div className="flex justify-between text-green-700"><span>Diskon</span><span>-{formatPrice(lastTransaction.discount)}</span></div>}
                  <div className="flex justify-between font-bold text-xs mt-0.5"><span>TOTAL</span><span>{formatPrice(lastTransaction.total)}</span></div>
                  <div className="border-b border-dashed border-black my-1.5" />
                  {lastTransaction.payment_method === "cash" ? (
                    <>
                      <div className="flex justify-between"><span>Bayar (Tunai)</span><span>{formatPrice(lastTransaction.cash_received)}</span></div>
                      <div className="flex justify-between font-bold"><span>Kembalian</span><span>{formatPrice(lastTransaction.cash_change)}</span></div>
                    </>
                  ) : lastTransaction.payment_method === "kupon" ? (
                    <>
                      <div className="flex justify-between font-bold"><span>Bayar (Kupon)</span><span>GRATIS</span></div>
                      {lastTransaction.redeem_coupon_code && <div className="flex justify-between text-[8px]"><span>Kode Kupon</span><span>{lastTransaction.redeem_coupon_code}</span></div>}
                    </>
                  ) : lastTransaction.payment_method === "edc" ? (
                    <>
                      <div className="flex justify-between"><span>Bayar (EDC Debit/Kredit)</span><span>{formatPrice(lastTransaction.total)}</span></div>
                      {lastTransaction.edc_card_last4 && <div className="flex justify-between text-[8px]"><span>Kartu</span><span>****{lastTransaction.edc_card_last4}</span></div>}
                      {lastTransaction.edc_approval_code && <div className="flex justify-between text-[8px]"><span>Approval Code</span><span>{lastTransaction.edc_approval_code}</span></div>}
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between"><span>Bayar (QRIS)</span><span>{formatPrice(lastTransaction.total)}</span></div>
                      {lastTransaction.qris_pos_order_id && <div className="flex justify-between text-[8px]"><span>Ref QRIS</span><span>{lastTransaction.qris_pos_order_id}</span></div>}
                    </>
                  )}
                  <div className="border-b border-dashed border-black my-2" />
                  {lastTransaction.new_coupon ? (
                    /* Coupon QR only — no transaction QR needed */
                    <div className="mt-1 border border-dashed border-black p-1.5" style={{display:"flex",flexDirection:"column",alignItems:"center",textAlign:"center"}}>
                      <p className="font-bold text-[9px]">KUPON LOYALITAS</p>
                      <p className="text-[7px] mb-1">Kumpulkan 5 kupon = 1x potong gratis!</p>
                      <QRCodeSVG value={lastTransaction.new_coupon} size={85} level="M" />
                      <p className="text-[8px] mt-0.5 font-bold">{lastTransaction.new_coupon}</p>
                    </div>
                  ) : (
                    /* Transaction QR — only when no coupon */
                    <div style={{display:"flex",flexDirection:"column",alignItems:"center",textAlign:"center"}}>
                      <QRCodeSVG value={lastTransaction.transaction_code} size={100} level="M" />
                      <p className="text-[8px] mt-0.5">{lastTransaction.transaction_code}</p>
                    </div>
                  )}
                  <div className="text-center mt-2">
                    <p className="text-[8px]">Terima kasih atas kunjungan Anda!</p>
                    <p className="text-[8px]">Kumpulkan 5 kupon layanan = 1x potong GRATIS</p>
                  </div>
                </>
              )}
            </div>
            <div className="p-4 bg-gray-100 flex gap-2 no-print">
              <Button onClick={handlePrint} className="flex-1 bg-black hover:bg-gray-800 text-white font-bold uppercase tracking-wider h-10 rounded-none" data-testid="print-receipt-btn">
                <Printer size={16} className="mr-2" /> Cetak Struk
              </Button>
              <Button onClick={() => setShowReceipt(false)} variant="outline" className="h-10 rounded-none border-gray-300 text-black" data-testid="close-receipt-btn">
                <X size={16} className="mr-2" /> Tutup
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Printer Settings */}
        <PrinterSettingsDialog open={printerOpen} onClose={() => { setPrinterOpen(false); refreshPrinterConfig(); }} />
      </div>
    </>
  );
}
